import { CheckIcon, CloseIcon } from "@chakra-ui/icons";
import { IconButtonProps } from "@chakra-ui/react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import useGetEnrollmentsPaginated from "../../hooks/api/enrollment/useGetEnrollmentsPaginated";
import useUpdateEnrollment from "../../hooks/api/enrollment/useUpdateEnrollment";
import { Enrollment } from "../../models/Enrollment";
import useAuthState from "../../state/auth.state";
import GenericTable from "./GenericTable";

export default function EnrollmentsTable() {
  const { t } = useTranslation();
  const { role } = useAuthState();
  const {
    data: enrollments,
    isPending,
    isError,
    mutateAsync: fetchEnrollments,
    variables,
  } = useGetEnrollmentsPaginated();
  const {
    mutateAsync: updateEnrollment,
    isPending: isUpdateEnrollmentPending,
    variables: updateEnrollmentVariables,
  } = useUpdateEnrollment();

  const extraActions: (enrollment: Enrollment) => IconButtonProps[] =
    useCallback(
      (enrollment) => {
        const actions: IconButtonProps[] = [];

        const doUpdate = (status: Enrollment["status"]) =>
          updateEnrollment({
            enrollmentId: enrollment.id,
            status,
          }).finally(() => fetchEnrollments({ page: variables?.page ?? 0 }));

        if (role === "ROLE_TEACHER" && enrollment.status === "PENDING") {
          actions.push({
            onClick: () => doUpdate("APPROVED"),
            bg: "green.100",
            color: "gren.800",
            "aria-label": t("common.buttons.approve"),
            icon: <CheckIcon />,
            isLoading:
              updateEnrollmentVariables?.status === "APPROVED" &&
              updateEnrollmentVariables?.enrollmentId === enrollment.id &&
              isUpdateEnrollmentPending,
          });
          actions.push({
            onClick: () => doUpdate("REJECTED"),
            bg: "red.100",
            color: "red.800",
            "aria-label": t("common.buttons.reject"),
            icon: <CloseIcon />,
            isLoading:
              updateEnrollmentVariables?.status === "REJECTED" &&
              updateEnrollmentVariables?.enrollmentId === enrollment.id &&
              isUpdateEnrollmentPending,
          });
        }

        return actions;
      },
      [
        fetchEnrollments,
        isUpdateEnrollmentPending,
        role,
        t,
        updateEnrollment,
        updateEnrollmentVariables?.enrollmentId,
        updateEnrollmentVariables?.status,
        variables?.page,
      ],
    );

  return (
    <GenericTable<Enrollment>
      items={enrollments?.content ?? []}
      canEdit={false}
      canDelete={false}
      headers={[
        t("tables.fullName"),
        t("tables.language"),
        t("tables.type"),
        t("tables.status"),
      ]}
      rowExtractors={[
        (enrollment) => enrollment.fullName,
        (enrollment) => enrollment.language.name,
        (enrollment) =>
          enrollment.type === "STUDENTS_ENROLLED"
            ? t("common.labels.enrollment")
            : t("common.labels.owner"),
        (enrollment) =>
          enrollment.status === "PENDING"
            ? t("common.labels.pending")
            : enrollment.status === "APPROVED"
              ? t("common.labels.approved")
              : t("common.labels.rejected"),
      ]}
      isLoading={isPending}
      isError={isError}
      pagination={enrollments?.page}
      refetch={fetchEnrollments}
      extraActions={extraActions}
    />
  );
}
